import { Button, Card, Space, Link, Input, Table, Modal, Message } from "@arco-design/web-react";
import OperationButton from "@/components/OperationButton";
import { Currency } from "@/components/Numeric";
import { EnumLabel, EnumSelect } from "@/components/Enum";
import { StatusDef, Status } from "@/enums/status";
import { useCallback, useEffect, useState } from "react";
import { CustomerAPI } from "@/apis/sales";
import { useNavigate } from "react-router-dom";
import { IconPlus, IconEdit, IconCheck, IconClose } from "@arco-design/web-react/icon";
import dayjs from "dayjs";
import { CustomerAdd } from "./customer-add";
import { CustomerEdit } from "./customer-edit";
import { CustomerInfo } from "./customer-info";

const BalanceLink = ({ value }) => {
    const [visible, setVisible] = useState(false);
    const [balanceDetails, setBalanceDetails] = useState([]);

    const onClick = async () => {
        const res = await CustomerAPI.balanceDetails(value.id);
        setBalanceDetails(res);
        setVisible(true);
    }

    return (
        <>
            <Link onClick={onClick}>
                <Currency value={value.balance} />
            </Link>
            <Modal title={value.customerName} style={{ width: 1100 }}
                visible={visible}
                onOk={() => setVisible(false)}
                onCancel={() => setVisible(false)}>
                <Table rowKey="id" columns={[
                    {
                        title: "Invoice#", dataIndex: "invoiceNo"
                    },
                    {
                        title: "Due Date",
                        dataIndex: "dueDate",
                    },
                    {
                        title: "Aging",
                        render: (col, record) => <span>{dayjs().diff(record.dueDate, 'day')} day(s)</span>
                    },
                    {
                        title: "Total Amount",
                        render: (col, record) => <Currency value={record.totalAmount} />
                    },
                    {
                        title: "Amount Due",
                        render: (col, record) => <Currency value={record.totalAmount} />
                    }
                ]} data={balanceDetails} />
            </Modal>
        </>
    );
}

export const Customer = () => {
    const navigate = useNavigate();
    const [customerName, setCustomerName] = useState("");
    const [status, setStatus] = useState(1);
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    const getCustomers = useCallback(async () => {
        let res = await CustomerAPI.query({ page, status, customerName });

        for (const index in res.records) {
            const customer = res.records[index];
            const balance = await CustomerAPI.balance(customer.id);
            customer.balance = balance;
        }

        setTotal(res.total);
        setData(res.records);
    }, [page, status, customerName]);

    useEffect(() => {
        getCustomers();
    }, [getCustomers]);

    return (
        <Card title="Customer" extra={
            <Space>
                <Input allowClear placeholder="Customer Name" value={customerName} onChange={(val) => setCustomerName(val)} />
                <EnumSelect enumObj={StatusDef} style={{ width: 100 }} value={status} allowAll placeholder="Status"
                    onChange={val => setStatus(val)}
                />
                <Button type="primary" icon={<IconPlus />} onClick={() => navigate("/workspace/sales/customer/add")}>Add New Customer</Button>
            </Space>
        }>
            <Table rowKey="id" columns={[
                {
                    title: "Customer Name", dataIndex: "customerName",
                    render: (col, record) => <Link onClick={() => navigate(`/workspace/sales/customer/${record.id}`)}>{record.customerName}</Link>
                },
                {
                    title: "Status",
                    render: (col, record) => <EnumLabel enumObj={StatusDef} value={record.status} />
                },
                {
                    title: "Balance",
                    render: (col, record) => <BalanceLink value={record} />
                },
                {
                    title: "Operations",
                    width: 120,
                    render: (col, record) => (
                        <Space size="mini">
                            <OperationButton icon={<IconEdit />} label="Edit"
                                onClick={() => navigate(`/workspace/sales/customer/${record.id}/edit`)} />
                            {record.status === Status.Active &&
                                <OperationButton icon={<IconClose />} label="Disable" status="danger"
                                    onClick={async () => {
                                        try {
                                            await CustomerAPI.disable(record.id);
                                            Message.success("Customer deactivated");
                                            getCustomers();
                                        } catch (error) {
                                            Message.error(error.message);
                                        }
                                    }} />
                            }
                            {record.status === Status.Inactive &&
                                <OperationButton icon={<IconCheck />} label="Enable" status="success"
                                    onClick={async () => {
                                        try {
                                            await CustomerAPI.enable(record.id);
                                            Message.success("Customer activated");
                                            getCustomers();
                                        } catch (error) {
                                            Message.error(error.message);
                                        }
                                    }} />
                            }
                        </Space>
                    )
                },
            ]} data={data} pagination={{ total, current: page, pageSize: 20 }} onChange={(pagination) => setPage(pagination.current)} />

        </Card>
    );
}

Customer.Add = CustomerAdd;
Customer.Edit = CustomerEdit;
Customer.Info = CustomerInfo;
